import Vue from "vue";
import pluralize from "pluralize";
import { REGEX_REPLACE_HTML_TAG } from "../constants/regex";
import { RADIO_OTHER_OPTIONS_PREFIX } from "../constants/prefix";
import { TIME_FROM_TO_SEPARATOR } from "../constants/separator";

Vue.mixin({
  methods: {
    // https://stackoverflow.com/questions/1026069/how-do-i-make-the-first-letter-of-a-string-uppercase-in-javascript
    // Used for trying to seamlessly log people in with capitalized email addresses
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    convertToSingularForm(input) {
      if (!input) {
        return "";
      }
      return pluralize.singular(input);
    },
    convertToPluralForm(input) {
      if (!input) {
        return "";
      }
      return pluralize.plural(input);
    },
    // If input string ending by a number, remove that number
    removeLastNumberFromString(input) {
      const tokens = input.split(" ");
      if (isNaN(tokens[tokens.length - 1])) {
        return input;
      } else {
        tokens.pop();
        return tokens.join(" ");
      }
    },
    convertToAbsolutePath(path) {
      if (!path) {
        return "";
      }
      const prefix =
        "https://enginehireio-static-and-media8.s3.amazonaws.com/media/";
      if (path.indexOf("https://enginehireio-static-and-media") === 0) {
        return path;
      }
      return prefix + path;
    },
    convertToRelativePath(path) {
      if (!path) {
        return "";
      }
      if (path.indexOf("s3.amazonaws.com/media/user_upload_files/") > 0) {
        return path.split("s3.amazonaws.com/media/")[1];
      }
      if (path.indexOf("s3.amazonaws.com/media/files/") > 0) {
        return path.split("s3.amazonaws.com/media/")[1];
      }
      return path;
    },
    encodeSpecialCharsFileUrl(inputStr) {
      const mapReplaced = {
        '+': '%2B',
        ',': '%2C',
        '@': '%40',
        '&': '%26',
        '$': '%24',
        '#': '%23'
      }
      return this.replaceAllByMap(inputStr, mapReplaced)
    },
    roundNumberTwoDigits(input) {
      if (!input) {
        return input;
      }
      return Math.round(input * 100) / 100;
    },
    roundNumberOneDigits(input) {
      if (!input) {
        return input;
      }
      return Math.round(input * 10) / 10;
    },
    formatPrice(value) {
      return this.roundNumberTwoDigits(Number(value)).toLocaleString('en-US');
    },
    /*
    Split an array into multiple chunks
    For example: 
      array: [a, b, c, d, e, f]
      numberOfChunk: 2
      ouput: [[a, b, c], [d, e, f]]
    */
    splitArrayIntoChunks(array, numberOfChunk) {
      if (array == null) {
        return [];
      }
      const chunk = Math.ceil(array.length / numberOfChunk);
      const result = [];
      let i, j, temporary;
      for (i = 0, j = array.length; i < j; i += chunk) {
        temporary = array.slice(i, i + chunk);
        result.push(temporary);
      }
      return result;
    },
    // Name include: first name + last name
    // Output: Only show first character of last name
    hideLastName(name) {
      const tokens = name.trim().split(/\s+/);
      const firstName = tokens[0];
      if (tokens.length > 1) {
        return firstName + " " + tokens[1][0].toUpperCase() + ".";
      } else {
        return firstName
      }
    },
    getDisplayFileName(filePath) {
      const tokens = filePath.split("/");
      if (tokens.length) {
        return tokens[tokens.length - 1];
      }
    },
    // Extract content of html tag
    getHtmlText(htmlString, breakLine = false) {
      if (!htmlString) {
        return "";
      }
      if (breakLine) {
        let result = htmlString.replace(REGEX_REPLACE_HTML_TAG, '\n');
        return result;
      } else {
        return htmlString.replace(REGEX_REPLACE_HTML_TAG, '').trim();
      }
    },
    convertUnderscoreToLabel(input) {
      if (!input) {
        return "";
      }
      const tokens = input.split("_");
      return tokens.map(token => this.capitalizeFirstLetter(token)).join(' ');
    },
    getNumberFromString(input) {
      if (!input) {
        return null;
      }
      let number = input.replace(/[^0-9\.]/g, '');
      if (number) {
        return Number(number);
      } else {
        return null;
      }
    },
    convertStringToBoolean(input) {
      if (!input) {
        return null;
      }
      const lowerInput = input.trim().toLowerCase();
      if (lowerInput === 'yes') {
        return true;
      } else if (lowerInput === 'no') {
        return false;
      }
      return null;
    },
    removeOtherRadioPrefix(value) {
      if (typeof (value) === 'string' && value.indexOf(RADIO_OTHER_OPTIONS_PREFIX) === 0) {
        return value.substring(RADIO_OTHER_OPTIONS_PREFIX.length);
      }
      return value;
    },
    getDisplayTrigger(template, type) {
      const isCandidate =
        template.selector1 === "Candidate" ||
        template.selector3 === "Candidate";
      const isClient =
        template.selector1 === "Client" ||
        template.selector3 === "Client";
      if (!isCandidate && !isClient) {
        return type;
      }
      if (["Status Change Notification", "Status Reminder"].includes(type)) {
        const labelStatus = isCandidate
          ? this.$store.getters.labelCandidateStatuses
          : this.$store.getters.labelEmployerStatuses;
        if (labelStatus) {
          return `${labelStatus} ${type}`;
        }
        return type;
      }
      if (
        [
          "Second Status Change Notification",
          "Second Status Reminder",
        ].includes(type)
      ) {
        const labelStatus = isCandidate
          ? this.$store.getters.labelCandidateSecondStatuses
          : this.$store.getters.labelEmployerSecondStatuses;
        if (labelStatus) {
          return `${labelStatus}${type.replace("Second", "")}`;
        }
        return type;
      }
      return type;
    },
    reverseString(str) {
      return str.split("").reverse().join("");
    },
    /*
    Ex:
    Input: +1234567890
    Format: (xxx) xxx xxx
    => Output: (234) 567 890
    */
    getDisplayPhone(phone) {
      const format = this.$store.getters.phoneDefaultFormat;
      if (!format || !phone) {
        return phone;
      }
      const mapReplacedVal = {
        ' ': '',
        '-': '',
        '(': '',
        ')': ''
      }
      const normalizedPhone = this.replaceAllByMap(phone, mapReplacedVal); //Remove all special characters
      let result = (this.reverseString(format)).toLowerCase();
      for (let index = normalizedPhone.length - 1; index >= 0; index--) {
        result = result.replace('x', normalizedPhone[index])
      }
      return this.reverseString(result);
    },
    getClassListColLayout(itemPerRow) {
      if (itemPerRow === 0) {
        return "col";
      }
      if (itemPerRow == null || itemPerRow === 1) {
        return "col-lg-12";
      }
      return "col-lg-" + String(12 / itemPerRow);
    },
    getDisplayDayHourSelector(value, listDays, field) {
      if (value == null || value.length === 0) {
        return [];
      }
      if (!listDays) {
        listDays = this.$store.getters.days;
      }
      // Convert list days from String to array
      const listWorkingDays = listDays
        .split("\n")
        .map((option) => option.trim())
        .filter((option) => option.length > 0);
      let result = [];
      // Get string corresponding with each day with format Day: From To
      for (let index = 0; index < listWorkingDays.length; index++) {
        const timeValue = value[index];
        if (timeValue == null || timeValue.length === 0) {
          continue;
        }
        let timeTokens;
        if (timeValue.includes(TIME_FROM_TO_SEPARATOR)) {
          // New format (use with textbox)
          timeTokens = timeValue.split(TIME_FROM_TO_SEPARATOR);
        } else {
          // Old format (use with Timepicker)
          timeTokens = timeValue.split(",");
          if (timeTokens.length === 1) {
            timeTokens.push("");
          }
          timeTokens = timeTokens.slice(0, 2);
        }
        const mapReplacedValues = {
          ":AM": " AM",
          ":PM": " PM",
        };
        const timeFrom = timeTokens[0]
          ? this.replaceAllByMap(timeTokens[0], mapReplacedValues)
          : "";
        const timeTo = timeTokens[1]
          ? this.replaceAllByMap(timeTokens[1], mapReplacedValues)
          : "";
        const item = {
          day: listWorkingDays[index],
          from: timeFrom,
          to: timeTo,
        };
        if (field) {
          if (field.hasTimeOff && timeTokens.length > 2) {
            item.timeOff = timeTokens[2];
          }
          if (field.hasNote) {
            if (field.hasTimeOff && timeTokens[3]) {
              item.note = timeTokens[3];
            } else if (timeTokens[2]) {
              item.note = timeTokens[2];
            }
          }
        }
        result.push(item);
      }
      return result;
    }
  }
});



// WEBPACK FOOTER //
// ./src/mixins/conversion.js